import { CATEGORY_LABEL, type NoteCardOut } from "@/lib/api";
import { CATEGORY_COLOR, folderColor } from "@/lib/palette";
import { cn } from "@/lib/utils";

/**
 * A card's folder/path indicator — `Markdown / Research / Interpretability`.
 *
 * Arbitrary depth: the category is the first segment and the item's folder path
 * supplies the rest, so nothing assumes a single parent. Each segment carries
 * its OWN colour (the server sends one per segment), which is what makes a
 * recoloured folder repaint everywhere it appears at once.
 *
 * Long paths ellipsize from the LEFT — the deepest folder is the one that
 * identifies the note, so `… / Research / Interpretability` is more useful than
 * `Markdown / Research / …`.
 */
export function FolderBreadcrumb({
  card,
  className,
  onSelect,
  tone = "dark",
}: {
  card: NoteCardOut;
  className?: string;
  /** Narrow the feed to this folder. Absent on surfaces where the breadcrumb is
   * purely informational. */
  onSelect?: (path: string) => void;
  /** The surface underneath. `"light"` prints the path in dark ink directly on
   * the page — which is what a light preview wants, since darkening the page
   * with a scrim to fit light text turns a clean sheet into a smudged one. */
  tone?: "dark" | "light";
}) {
  const onLight = tone === "light";
  // Both arrays are read through a guard: this is a PWA whose cached bundle can
  // meet an API older or newer than it was built against, and a card that
  // cannot draw its path should still draw its title.
  const path = card.folder_path ?? [];
  const colors = card.folder_colors ?? [];
  const segments = [
    { label: CATEGORY_LABEL[card.category], color: CATEGORY_COLOR[card.category], path: "" },
    ...path.map((label, i) => ({
      label,
      color: folderColor(colors[i]),
      path: path.slice(0, i + 1).join("/"),
    })),
  ];

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-1 font-mono text-[9.5px] leading-none tracking-[0.06em]",
        className,
      )}
      // The whole path in one place for anyone who can't read the truncation.
      title={segments.map((s) => s.label).join(" / ")}
    >
      {segments.map((segment, i) => {
        const last = i === segments.length - 1;
        const interactive = !!onSelect && !!segment.path;
        return (
          <span key={`${segment.path}-${segment.label}`} className="flex min-w-0 items-center gap-1">
            {i > 0 && (
              <span className={cn("shrink-0", onLight ? "text-black/30" : "opacity-40")}>/</span>
            )}
            <span
              aria-hidden
              className="h-[6px] w-[6px] shrink-0 rounded-[2px]"
              style={{ background: segment.color }}
            />
            {interactive ? (
              <button
                type="button"
                onClick={(e) => {
                  // The card itself opens the note; a breadcrumb click must not
                  // do both.
                  e.stopPropagation();
                  e.preventDefault();
                  onSelect?.(segment.path);
                }}
                className={cn(
                  "truncate rounded-sm px-0.5 transition-colors hover:underline",
                  onLight
                    ? last
                      ? "text-black/75 hover:text-black"
                      : "text-black/50 hover:text-black/80"
                    : last
                      ? "text-foreground/85 hover:text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                )}
              >
                {segment.label}
              </button>
            ) : (
              <span
                className={cn(
                  "truncate",
                  onLight
                    ? last
                      ? "text-black/75"
                      : "text-black/50"
                    : last
                      ? "text-foreground/85"
                      : "text-muted-foreground",
                )}
              >
                {segment.label}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
